import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Table, Button, Modal, Form, Input, Typography, Tag, Space,
  Tooltip, message, Alert, Card,
} from 'antd'
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons'
import axiosInstance from '@/lib/axios'

const { Text, Paragraph } = Typography
const { TextArea } = Input

const BASE = '/system-settings/messaggi-template'

interface MessaggioTemplate {
  id: number
  chiave: string
  nome: string
  descrizione?: string
  testo: string
  tag_disponibili?: string
  updated_at?: string
}

// Tag colorati per la preview
const TAG_COLOR: Record<string, string> = {
  nome: 'blue', cognome: 'blue',
  targa: 'green', marca: 'green', modello: 'green',
  data: 'orange', ora: 'orange',
  tipo_servizio: 'purple',
  codice_prenotazione: 'cyan',
  numero_scheda: 'cyan',
  importo_preventivo: 'gold',
  tipo_manutenzione: 'purple',
  data_scadenza: 'orange',
  km_scadenza: 'orange',
  targa_cortesia: 'green',
  modello_cortesia: 'green',
  data_restituzione: 'orange',
}

function TagBadges({ tags }: { tags: string }) {
  return (
    <Space wrap size={4}>
      {tags.split(',').map(t => {
        const k = t.trim()
        return <Tag key={k} color={TAG_COLOR[k] ?? 'default'}>{`{{${k}}}`}</Tag>
      })}
    </Space>
  )
}

function TesroPreview({ testo }: { testo: string }) {
  const parts = testo.split(/(\{\{[^}]+\}\})/)
  return (
    <span>
      {parts.map((p, i) => {
        const m = p.match(/^\{\{(\w+)\}\}$/)
        if (m) return <Tag key={i} color={TAG_COLOR[m[1]] ?? 'default'} style={{ margin: '0 1px' }}>{p}</Tag>
        return <span key={i}>{p}</span>
      })}
    </span>
  )
}

export default function MessaggiTemplateManager() {
  const qc = useQueryClient()
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [form] = Form.useForm()

  const { data: templates = [], isLoading } = useQuery<MessaggioTemplate[]>({
    queryKey: ['messaggi-template'],
    queryFn: () => axiosInstance.get(BASE).then(r => r.data),
  })

  const updateMutation = useMutation({
    mutationFn: ({ chiave, testo }: { chiave: string; testo: string }) =>
      axiosInstance.put(`${BASE}/${chiave}`, { testo }),
    onSuccess: () => {
      message.success('Template salvato')
      qc.invalidateQueries({ queryKey: ['messaggi-template'] })
      setEditingKey(null)
    },
    onError: () => message.error('Errore nel salvataggio'),
  })

  const editing = templates.find(t => t.chiave === editingKey)

  const columns = [
    {
      title: 'Funzione',
      key: 'nome',
      width: 220,
      render: (_: unknown, r: MessaggioTemplate) => (
        <Space direction="vertical" size={2}>
          <Text strong>{r.nome}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.chiave}</Text>
        </Space>
      ),
    },
    {
      title: 'Testo corrente',
      key: 'testo',
      render: (_: unknown, r: MessaggioTemplate) => (
        <Paragraph style={{ margin: 0, fontSize: 12 }} ellipsis={{ rows: 2 }}>
          <TesroPreview testo={r.testo} />
        </Paragraph>
      ),
    },
    {
      title: 'Tag disponibili',
      key: 'tag',
      width: 300,
      render: (_: unknown, r: MessaggioTemplate) =>
        r.tag_disponibili ? <TagBadges tags={r.tag_disponibili} /> : '—',
    },
    {
      title: '',
      key: 'azioni',
      width: 60,
      render: (_: unknown, r: MessaggioTemplate) => (
        <Tooltip title="Modifica testo">
          <Button size="small" icon={<EditOutlined />} onClick={() => {
            setEditingKey(r.chiave)
            form.setFieldsValue({ testo: r.testo })
          }} />
        </Tooltip>
      ),
    },
  ]

  return (
    <Card>
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        style={{ marginBottom: 16 }}
        message="Come usare i template"
        description={
          <span>
            Inserisci i tag tra doppie parentesi graffe per includere i dati del cliente/veicolo/appuntamento.
            Esempio: <Text code>{'Gentile {{nome}} {{cognome}}, appuntamento il {{data}} alle {{ora}}'}</Text>.
            Il template viene usato al posto del messaggio AI — se vuoi tornare all'AI, svuota il testo.
          </span>
        }
      />

      <Table
        rowKey="chiave"
        dataSource={templates}
        columns={columns}
        loading={isLoading}
        pagination={false}
        size="middle"
      />

      <Modal
        title={editing ? `Modifica: ${editing.nome}` : ''}
        open={!!editingKey}
        onCancel={() => setEditingKey(null)}
        onOk={() => form.submit()}
        okText="Salva"
        confirmLoading={updateMutation.isPending}
        width={620}
      >
        {editing && (
          <>
            {editing.descrizione && (
              <Paragraph type="secondary" style={{ marginBottom: 12 }}>
                {editing.descrizione}
              </Paragraph>
            )}
            {editing.tag_disponibili && (
              <div style={{ marginBottom: 12 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>Tag disponibili (clicca per copiare):</Text>
                <div style={{ marginTop: 6 }}>
                  <TagBadges tags={editing.tag_disponibili} />
                </div>
              </div>
            )}
            <Form form={form} onFinish={v => updateMutation.mutate({ chiave: editing.chiave, testo: v.testo })}>
              <Form.Item
                name="testo"
                rules={[{ required: true, message: 'Il testo è obbligatorio' }]}
              >
                <TextArea
                  rows={5}
                  placeholder={`Gentile {{nome}} {{cognome}}, la ricordiamo del suo appuntamento...`}
                  maxLength={320}
                  showCount
                />
              </Form.Item>
            </Form>
          </>
        )}
      </Modal>
    </Card>
  )
}
